import { useMemo, useState, useSyncExternalStore, type FormEvent } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ApiError, apiRequest, getSnapshot, subscribe } from "./client";
import { ApiErrorPanel, useApi, useBrandScope } from "./shared";
import type { CurrentUser, UserRole } from "./types";

interface AdminUser {
  id: string;
  email: string;
  role: UserRole;
  brandId: number | null;
  enabled: boolean;
  passwordChangeRequired: boolean;
  createdAt: string;
}

const roleLabels: Record<UserRole, string> = {
  PLATFORM_ADMIN: "Platform admin",
  BRAND_ADMIN: "Brand admin",
  BRAND_VIEWER: "Brand viewer",
};

type CreateField = "email" | "role" | "brandId" | "temporaryPassword";
type FieldErrors = Partial<Record<CreateField, string>>;

function creationErrors(error: unknown): FieldErrors {
  if (!(error instanceof ApiError)) return {};
  const errors: FieldErrors = {};
  for (const violation of error.violations) {
    const field = violation.field.split(".").pop();
    if (
      field === "email" ||
      field === "role" ||
      field === "brandId" ||
      field === "temporaryPassword"
    ) {
      errors[field] = violation.message;
    }
  }
  if (error.error === "email_taken") {
    errors.email = "An account already uses this email address.";
  } else if (error.error === "invalid_brand") {
    errors.brandId = "Enter a valid brand ID for this account.";
  } else if (error.error === "weak_password") {
    errors.temporaryPassword =
      "This password does not meet the server's password requirements.";
  }
  return errors;
}

function CreateUserDialog({
  currentUser,
  initialBrandId,
  onClose,
  onCreated,
}: {
  currentUser: CurrentUser;
  initialBrandId?: number;
  onClose: () => void;
  onCreated: (user: AdminUser) => void;
}) {
  const platformAdmin = currentUser.role === "PLATFORM_ADMIN";
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<UserRole>("BRAND_VIEWER");
  const [brandId, setBrandId] = useState(
    String((platformAdmin ? initialBrandId : currentUser.brandId) ?? ""),
  );
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);

  const clearFieldError = (field: CreateField) => {
    setFieldErrors((previous) => ({ ...previous, [field]: undefined }));
  };
  const close = () => {
    if (saving) return;
    setTemporaryPassword("");
    onClose();
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (saving) return;
    const errors: FieldErrors = {};
    const normalizedEmail = email.trim();
    const passwordBytes = new TextEncoder().encode(temporaryPassword).length;
    if (!/^[^\s@]+@[^\s@]+$/.test(normalizedEmail)) {
      errors.email = "Enter a valid email address.";
    }
    if (passwordBytes < 12 || passwordBytes > 72) {
      errors.temporaryPassword =
        "Use a password between 12 and 72 UTF-8 bytes.";
    }
    if (
      platformAdmin &&
      role !== "PLATFORM_ADMIN" &&
      (!Number.isSafeInteger(Number(brandId)) || Number(brandId) <= 0)
    ) {
      errors.brandId = "Enter a positive whole-number brand ID.";
    }
    setFieldErrors(errors);
    setError(null);
    if (Object.keys(errors).length > 0) return;
    setSaving(true);
    try {
      const created = await apiRequest<AdminUser>("/api/admin/users", {
        method: "POST",
        json: {
          email: normalizedEmail,
          role,
          ...(platformAdmin && role !== "PLATFORM_ADMIN"
            ? { brandId: Number(brandId) }
            : {}),
          temporaryPassword,
        },
      });
      setTemporaryPassword("");
      onCreated(created);
    } catch (failure) {
      setFieldErrors(creationErrors(failure));
      setError(failure);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={close} fullWidth maxWidth="sm">
      <Box component="form" onSubmit={submit} noValidate>
        <DialogTitle>Create backoffice user</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <DialogContentText>
              The user must change their temporary password on first sign-in.
              Share it with them securely.
            </DialogContentText>
            {error != null && <ApiErrorPanel error={error} />}
            <TextField
              label="Email"
              type="email"
              autoComplete="off"
              autoFocus
              required
              fullWidth
              disabled={saving}
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                clearFieldError("email");
              }}
              error={Boolean(fieldErrors.email)}
              helperText={fieldErrors.email}
            />
            <TextField
              label="Role"
              select
              required
              fullWidth
              disabled={saving}
              value={role}
              onChange={(event) => {
                setRole(event.target.value as UserRole);
                clearFieldError("role");
                clearFieldError("brandId");
              }}
              error={Boolean(fieldErrors.role)}
              helperText={fieldErrors.role}
            >
              {Object.entries(roleLabels)
                .filter(
                  ([value]) => platformAdmin || value !== "PLATFORM_ADMIN",
                )
                .map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
            </TextField>
            {role !== "PLATFORM_ADMIN" && (
              <TextField
                label="Brand ID"
                required
                fullWidth
                disabled={saving || !platformAdmin}
                value={brandId}
                slotProps={{ htmlInput: { inputMode: "numeric" } }}
                onChange={(event) => {
                  setBrandId(event.target.value);
                  clearFieldError("brandId");
                }}
                error={Boolean(fieldErrors.brandId)}
                helperText={
                  fieldErrors.brandId ||
                  (!platformAdmin
                    ? "New users belong to your brand."
                    : undefined)
                }
              />
            )}
            <TextField
              label="Temporary password"
              type="password"
              autoComplete="new-password"
              required
              fullWidth
              disabled={saving}
              value={temporaryPassword}
              onChange={(event) => {
                setTemporaryPassword(event.target.value);
                clearFieldError("temporaryPassword");
              }}
              error={Boolean(fieldErrors.temporaryPassword)}
              helperText={
                fieldErrors.temporaryPassword ||
                "12–72 UTF-8 bytes. Non-ASCII characters can use multiple bytes."
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? "Creating…" : "Create user"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

function UserStatusDialog({
  user,
  currentUserId,
  onClose,
  onUpdated,
}: {
  user: AdminUser;
  currentUserId: string;
  onClose: () => void;
  onUpdated: (user: AdminUser) => void;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const isSelf = user.id === currentUserId;
  const update = async () => {
    if (saving || isSelf) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await apiRequest<AdminUser>(
        `/api/admin/users/${encodeURIComponent(user.id)}`,
        { method: "PATCH", json: { enabled: !user.enabled } },
      );
      onUpdated(updated);
    } catch (failure) {
      setError(failure);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={() => !saving && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>{user.enabled ? "Disable" : "Enable"} user?</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText>
            {user.enabled
              ? `Disable ${user.email}? They will no longer be able to sign in or refresh their session. Existing access tokens may remain usable for up to 15 minutes.`
              : `Enable ${user.email}? They will be able to sign in again with their existing credentials.`}
          </DialogContentText>
          {isSelf && (
            <Alert severity="warning">
              You cannot change your own account status.
            </Alert>
          )}
          {error != null && <ApiErrorPanel error={error} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={user.enabled ? "error" : "primary"}
          onClick={() => void update()}
          disabled={saving || isSelf}
        >
          {saving ? "Saving…" : `${user.enabled ? "Disable" : "Enable"} user`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function UsersContent({ currentUser }: { currentUser: CurrentUser }) {
  const { brandId } = useBrandScope();
  const { data, loading, error, reload } =
    useApi<AdminUser[]>("/api/admin/users");
  const [emailFilter, setEmailFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [createOpen, setCreateOpen] = useState(false);
  const [statusUser, setStatusUser] = useState<AdminUser | null>(null);
  const [notice, setNotice] = useState("");
  const filteredUsers = useMemo(() => {
    const term = emailFilter.trim().toLocaleLowerCase();
    return (data || []).filter(
      (user) =>
        (brandId === undefined || user.brandId === brandId) &&
        (!term || user.email.toLocaleLowerCase().includes(term)),
    );
  }, [data, brandId, emailFilter]);
  const displayedPage = Math.min(
    page,
    Math.max(0, Math.ceil(filteredUsers.length / rowsPerPage) - 1),
  );

  return (
    <Stack spacing={2} sx={{ p: { xs: 2, md: 3 } }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
      >
        <Box>
          <Typography variant="h5" component="h1">
            Backoffice users
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage administrator access and account status.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button onClick={reload} disabled={loading}>
            Refresh
          </Button>
          <Button variant="contained" onClick={() => setCreateOpen(true)}>
            Create user
          </Button>
        </Stack>
      </Stack>
      {notice && (
        <Alert severity="success" onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}
      <TextField
        label="Filter by email"
        size="small"
        value={emailFilter}
        onChange={(event) => {
          setEmailFilter(event.target.value);
          setPage(0);
        }}
        sx={{ maxWidth: 420 }}
        helperText="Filters the users returned for your access and selected brand."
      />
      {loading && <LinearProgress aria-label="Loading users" />}
      {error != null && <ApiErrorPanel error={error} onRetry={reload} />}
      {!error && data && (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small" aria-label="Backoffice users">
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Password change</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 5, textAlign: "center" }}>
                      {data.length === 0
                        ? "No backoffice users are available."
                        : "No users match the selected brand and email filter."}
                    </TableCell>
                  </TableRow>
                )}
                {filteredUsers
                  .slice(
                    displayedPage * rowsPerPage,
                    (displayedPage + 1) * rowsPerPage,
                  )
                  .map((user) => {
                    const isSelf = user.id === String(currentUser.userId);
                    return (
                      <TableRow key={user.id}>
                        <TableCell>
                          {user.email}
                          {isSelf && (
                            <Chip
                              size="small"
                              label="You"
                              variant="outlined"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {roleLabels[user.role] || user.role}
                        </TableCell>
                        <TableCell>{user.brandId ?? "All brands"}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={user.enabled ? "success" : "default"}
                            label={user.enabled ? "Enabled" : "Disabled"}
                          />
                        </TableCell>
                        <TableCell>
                          {user.passwordChangeRequired
                            ? "Required"
                            : "Complete"}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {user.createdAt
                            ? new Date(user.createdAt).toLocaleString()
                            : "—"}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip
                            title={
                              isSelf
                                ? "You cannot change your own account status."
                                : ""
                            }
                          >
                            <span>
                              <Button
                                size="small"
                                color={user.enabled ? "error" : "primary"}
                                disabled={isSelf}
                                onClick={() => setStatusUser(user)}
                              >
                                {user.enabled ? "Disable" : "Enable"}
                              </Button>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredUsers.length}
            page={displayedPage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value));
              setPage(0);
            }}
          />
        </Paper>
      )}
      {createOpen && (
        <CreateUserDialog
          currentUser={currentUser}
          initialBrandId={brandId}
          onClose={() => setCreateOpen(false)}
          onCreated={(user) => {
            setCreateOpen(false);
            setNotice(
              `${user.email} was created. A password change is required at first sign-in.`,
            );
            reload();
          }}
        />
      )}
      {statusUser && (
        <UserStatusDialog
          user={statusUser}
          currentUserId={String(currentUser.userId)}
          onClose={() => setStatusUser(null)}
          onUpdated={(user) => {
            setStatusUser(null);
            setNotice(
              `${user.email} is now ${user.enabled ? "enabled" : "disabled"}.`,
            );
            reload();
          }}
        />
      )}
    </Stack>
  );
}

export function UsersPage() {
  const { user } = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  if (!user || user.role === "BRAND_VIEWER") {
    return (
      <Alert severity="warning" sx={{ m: 3 }}>
        Only platform and brand administrators can manage backoffice users.
      </Alert>
    );
  }
  return <UsersContent key={String(user.userId)} currentUser={user} />;
}
